import { useSelector } from 'react-redux';
import { getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import { useErrorOnNotOkFetchye } from '@americanexpress/fetchye-amex';
import { v4 } from 'uuid';
import { ignoreHeadersByKey } from 'fetchye';
import {
  EARLY_TENURE_BUSINESS_GOLD_URL_REDIRECT,
  EARLY_TENURE_BUSINESS_PLAT_URL_REDIRECT,
  EARLY_TENURE_CONSUMER_GOLD_URL_REDIRECT,
  EARLY_TENURE_CONSUMER_PLAT_URL_REDIRECT,
} from '../constants/pathnameConstants';
import { getAkamaiDomainSelector } from '../selectors/getDomainSelectors';
import { useAccountToken } from './membershipDomainDataHooks';
import { getUrlSelectors } from '../selectors/getUrlSelectors';
import { HIPED_ENC_TYPE, TLS_ONLINE_CLIENT_ID } from '../constants/travelerDataConstants';
import { useFeatureFlagWithProperties } from '../thunks/useFeatureFlagWithProperties';
import { EARLY_TENURE_FF } from '../constants/oneXpConstants';

export const useSegmentationApi = () => {
  const url = useSelector(getUrlSelectors);
  const accountToken = useAccountToken();
  const isEarlyTenureTileMarket = useFeatureFlagWithProperties(EARLY_TENURE_FF);

  const {
    data: segmentationApiData,
    isLoading,
    error,
  } = useErrorOnNotOkFetchye(
    url,
    {
      defer: !accountToken || !isEarlyTenureTileMarket,
      method: 'POST',
      mapOptionsToKey: ignoreHeadersByKey(['correlationId']),
      credentials: 'include',
      headers: {
        encryption_type: HIPED_ENC_TYPE,
        clientId: TLS_ONLINE_CLIENT_ID,
        correlationId: v4(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        context: [{
          code: 'INSIGHTS_TYPE',
          value: 'SEGMENTATION',
        }],
        card: {
          cardNumber: accountToken,
          type: 'ENC_CKMS',
        },
      }),
    },
    {
    }
  );

  return {
    isLoading,
    error,
    data: segmentationApiData?.body,
  };
};

export const useGetEarlyTenureGroup = () => {
  const { data: segmentationData, isLoading, error } = useSegmentationApi();

  if (error || isLoading) {
    return null;
  }

  return segmentationData
    ?.insightsData?.find((obj) => obj.segmentation)
    ?.segmentation?.customer_segments
    ?.filter((segment) => segment.group_name === 'Early_Tenure_Segment')[0]?.group_values[0] ?? null;
};

export const useGetCardProductGroup = () => {
  const { data: segmentationData, isLoading, error } = useSegmentationApi();

  if (error || isLoading) {
    return null;
  }

  return segmentationData
    ?.insightsData?.find((obj) => obj.segmentation)
    ?.segmentation?.customer_segments
    ?.filter((segment) => segment.group_name === 'Card_Product_Group')[0]?.group_values[0] ?? null;
};

export const useGetEarlyTenureSubheadingData = () => {
  const locale = useSelector(getActiveLocaleSelector);
  const akamaiDomain = useSelector(getAkamaiDomainSelector);
  const cardProductGroup = useGetCardProductGroup();

  switch (cardProductGroup?.key) {
    case 'BP':
      return {
        localeId: 'earlyTenure.businessPlatinum',
        redirectUrl: `${akamaiDomain}/${locale.toLowerCase()}/${EARLY_TENURE_BUSINESS_PLAT_URL_REDIRECT}`,
      };
    case 'BG':
      return {
        localeId: 'earlyTenure.businessGold',
        redirectUrl: `${akamaiDomain}/${locale.toLowerCase()}/${EARLY_TENURE_BUSINESS_GOLD_URL_REDIRECT}`,
      };
    case 'CP':
      return {
        localeId: 'earlyTenure.consumerPlatinum',
        redirectUrl: `${akamaiDomain}/${locale.toLowerCase()}/${EARLY_TENURE_CONSUMER_PLAT_URL_REDIRECT}`,
      };
    case 'CG':
      return {
        localeId: 'earlyTenure.consumerGold',
        redirectUrl: `${akamaiDomain}/${locale.toLowerCase()}/${EARLY_TENURE_CONSUMER_GOLD_URL_REDIRECT}`,
      };
    default:
      return {
        localeId: 'earlyTenure.consumerGold',
        redirectUrl: `${akamaiDomain}/${locale.toLowerCase()}/${EARLY_TENURE_CONSUMER_GOLD_URL_REDIRECT}`,
      };
  }
};

export const useSegmentationData = () => {
  const { localeId, redirectUrl } = useGetEarlyTenureSubheadingData();
  const earlyTenureGroup = useGetEarlyTenureGroup();
  const cardProductGroup = useGetCardProductGroup();

  return {
    earlyTenureGroup,
    cardProductGroup,
    localeId,
    redirectUrl,
  };
};
